import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import DeepLinkCopiedModal from './DeepLinkCopiedModal';
import { SeedIconCircleSmall } from '../../../../icons';
import { Text } from '../../common/Text/Text';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import { useDeepLink } from '../../../../../hooks/useDeepLink';
import { appLinks } from '../../../../../utils/webConfig';
import { isAppleDevice } from '../../common/utils';
import {
    viewEvent,
    clickEvent,
    successEvent,
} from '../../../../../hooks/PushEvents/clevertapEvents';
import { useDonorProfile } from '../../../../../api';
import type { Donee, SeedOfGoodResponse } from '../../../../../api';
import { colors } from '../../../../theme';
import {
    seedClickIosModalEventPayload,
    seedCloseIosModalEventPayload,
    seedDeepLinkSubTitle,
    seedDeepLinkTitle,
    seedSuccessIosModalEventPayload,
    seedViewIosModalEventPayload,
} from './SeedModal';
import { numberWithCommas } from '../../../../../utils/functions';
import { Button } from '../../../../ui';

const handleDownloadButton = () => {
    // copyDoneeIdToClipboard();
    window.location.href = isAppleDevice()
        ? appLinks.seedModal.apple
        : appLinks.seedModal.android;
};

const eventPayload = {
    name: 'ClaimSeedsOnApp',
    location: 'ThankYouScreen',
    type: 'Button',
    action: 'clicked',
};

const noPointsCtaEventPayload = {
    name: 'NoPointsGetApp',
    location: 'ThankYouScreen',
    type: 'Button',
    app: 'OGA',
};

interface ShowSeedProps {
    donee: Donee;
    seedsBalance: number;
    seeds: SeedOfGoodResponse | undefined;
}

const StyledDiv = styled.div`
    padding: 24px 24px 0 16px;
    border-top: 1px solid ${colors.utilitySeparatorColor};
    .seed-icon-container {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 16px;
        .seed-icon-wrapper {
        }
        .seed-text {
            text-align: left;
            font-size: 14px;
            font-style: normal;
            font-weight: 700;
            line-height: 20px;
            letter-spacing: 0.25px;
        }
    }
`;

const ShowSeed: React.FC<ShowSeedProps> = ({ donee, seedsBalance, seeds }) => {
    const { pushEvent } = usePushEvents();
    const { donorProfile } = useDonorProfile();
    const [url, setUrl] = useState<string | undefined>(undefined);
    const { deepLinkState, getDeepLink, showCopiedModal, setShowCopiedModal } =
        useDeepLink(
            clickEvent,
            seedsBalance > 0 ? eventPayload : noPointsCtaEventPayload,
            handleDownloadButton,
        );

    const title = useMemo(() => {
        if (seedsBalance > 0) {
            return 'Send a free bonus donation in the app with your Seeds of Good';
        }
        return 'Give in the app to start earning Seeds of Good!';
    }, [seedsBalance]);

    const ctaText = useMemo(() => {
        if (seedsBalance > 0) {
            return `Claim ${numberWithCommas(seedsBalance)} Seeds in App`;
        }
        return 'Get the Givelify App';
    }, [seedsBalance]);

    useEffect(() => {
        pushEvent(viewEvent, {
            name:
                seedsBalance > 0
                    ? 'PointsThankYouScreenPrompt'
                    : 'NoPointsThankYouScreenPrompt',
            location: 'ThankYouScreen',
            action: 'shown',
        });
    }, [pushEvent, seedsBalance]);

    const handleDownloadClicked = useCallback(() => {
        getDeepLink(donee, donorProfile, '/dl/points', {
            key: 'points',
            data: seeds || {},
        }).then((s) => {
            s && setUrl(s);
        });
    }, [getDeepLink, donee, donorProfile, seeds]);

    return (
        <>
            <StyledDiv id="thank-you-seeds">
                <div
                    id="thank-you-seeds-summary"
                    className="seed-icon-container"
                >
                    <div
                        id="thank-you-seeds-icon-container"
                        className="seed-icon-wrapper"
                    >
                        <SeedIconCircleSmall id="thank-you-seeds-icon" />
                    </div>
                    <Text
                        id="thank-you-seeds-title"
                        className="seed-text"
                    >
                        {title}
                    </Text>
                </div>
                <Button
                    id="thank-you-seeds-app-button"
                    color="primary"
                    loading={deepLinkState.loading}
                    onClick={handleDownloadClicked}
                    className="w-full"
                >
                    {ctaText}
                </Button>
            </StyledDiv>
            <DeepLinkCopiedModal
                id="thank-you-seeds-deep-link-modal"
                url={url}
                open={showCopiedModal}
                title={seedDeepLinkTitle}
                subTitle={seedDeepLinkSubTitle}
                viewEvent={viewEvent}
                clickEvent={clickEvent}
                successEvent={successEvent}
                viewEventPayload={seedViewIosModalEventPayload}
                clickEventPayload={seedClickIosModalEventPayload}
                successEventPayload={seedSuccessIosModalEventPayload}
                closeEventPayload={seedCloseIosModalEventPayload}
                onClick={handleDownloadButton}
                onClose={() => setShowCopiedModal(false)}
            />
        </>
    );
};

export default ShowSeed;
